import { List, Map } from 'immutable';
import { omit, trim } from 'lodash';
import { EntityTypesEnum } from '../../app.enum';
import {
  hasValue,
  IChangeField,
  IIgnoreField,
} from '../../utils/helpers/model.helper';
import {
  CONTENT_KEY,
  entityKeys,
  entityModelFactory,
  generateContent,
} from '../entity-data/entity.model';
import { AddressData } from '../geolocation-data/models/address-data.model';
import { Total } from './sub-models/total.model';
import { AchCreditBankDetails } from '../document-data/sub-models/ach-credit-bank-details.model';

export const PARENT_SCHEMA = Object.freeze({
  TOTALS: List<Total>([]),
  ACH_CREDIT_BANK_DETAILS: null,
});
export const CONTENT_SCHEMA = Object.freeze({
  BILLING_NAME: '',
  EMAIL: '',
  BILLING_ADDRESS: '',
  BILLING_ADDRESS_DATA: AddressData,
  SHIPPING_ADDRESS_SAME_AS_BILLING: true,
  SHIPPING_ADDRESS: '',
  SHIPPING_ADDRESS_DATA: AddressData,
  CONTACT_NAME: '',
  PHONE: '',
  MOBILE: '',
  WEBSITE: '',
  TAX_NUMBER: '',
  TERMS: null,
  NOTES: '',
  LOCALE: '',
});

const KEYS: any = entityKeys(PARENT_SCHEMA, CONTENT_SCHEMA);
const CONTENT_KEYS: any = generateContent(CONTENT_SCHEMA).KEYS;
const parentChanges = (props: Map<string, any>): IChangeField[] => {
  const totals = props.get(KEYS.TOTALS);
  const achCreditBankDetails = props.get(KEYS.ACH_CREDIT_BANK_DETAILS);

  return [
    {
      key: KEYS.TOTALS,
      value: totals
        ? totals.map((params) => new Total(params))
        : List<Total>([]),
    },
    {
      key: KEYS.ACH_CREDIT_BANK_DETAILS,
      value: achCreditBankDetails
        ? new AchCreditBankDetails(achCreditBankDetails)
        : null,
    },
  ];
};
const contentChanges = (contentProps: Map<string, any>): IChangeField[] => {
  const billingAddressData = contentProps.get(
    CONTENT_KEYS.BILLING_ADDRESS_DATA
  );
  const shippingAddressData = contentProps.get(
    CONTENT_KEYS.SHIPPING_ADDRESS_DATA
  );

  return [
    {
      key: CONTENT_KEYS.BILLING_ADDRESS_DATA,
      value: billingAddressData ? new AddressData(billingAddressData) : null,
    },
    {
      key: CONTENT_KEYS.SHIPPING_ADDRESS_DATA,
      value: shippingAddressData ? new AddressData(shippingAddressData) : null,
    },
  ];
};

export class Client extends entityModelFactory(
  PARENT_SCHEMA,
  parentChanges,
  CONTENT_SCHEMA,
  contentChanges
) {
  static ENTITY_TYPE: EntityTypesEnum = EntityTypesEnum.client;
  static KEYS: any = KEYS;
  static CONTENT_KEYS: any = CONTENT_KEYS;

  get ENTITY_TYPE(): string {
    return Client.ENTITY_TYPE;
  }

  get billingName(): string {
    return this.getFieldInContent(CONTENT_KEYS.BILLING_NAME);
  }

  get email(): string {
    return this.getFieldInContent(CONTENT_KEYS.EMAIL);
  }

  get billingAddress(): string {
    return this.getFieldInContent(CONTENT_KEYS.BILLING_ADDRESS);
  }

  get billingAddressData(): AddressData {
    return this.getFieldInContent(CONTENT_KEYS.BILLING_ADDRESS_DATA);
  }

  get shippingAddressSameAsBilling(): boolean {
    return this.getFieldInContent(
      CONTENT_KEYS.SHIPPING_ADDRESS_SAME_AS_BILLING
    );
  }

  get shippingAddress(): string {
    return this.getFieldInContent(CONTENT_KEYS.SHIPPING_ADDRESS);
  }

  get shippingAddressData(): AddressData {
    return this.getFieldInContent(CONTENT_KEYS.SHIPPING_ADDRESS_DATA);
  }

  get contactName(): string {
    return this.getFieldInContent(CONTENT_KEYS.CONTACT_NAME);
  }

  get phone(): string {
    return this.getFieldInContent(CONTENT_KEYS.PHONE);
  }

  get mobile(): string {
    return this.getFieldInContent(CONTENT_KEYS.MOBILE);
  }

  get website(): string {
    return this.getFieldInContent(CONTENT_KEYS.WEBSITE);
  }

  get taxNumber(): string {
    return this.getFieldInContent(CONTENT_KEYS.TAX_NUMBER);
  }

  get terms(): number {
    return this.getFieldInContent(CONTENT_KEYS.TERMS);
  }

  get notes(): string {
    return this.getFieldInContent(CONTENT_KEYS.NOTES);
  }

  get locale(): string {
    return this.getFieldInContent(CONTENT_KEYS.LOCALE);
  }

  get totals(): List<Total> {
    return this.get(KEYS.TOTALS);
  }

  get achCreditBankDetails(): AchCreditBankDetails | null {
    return this.get(KEYS.ACH_CREDIT_BANK_DETAILS);
  }

  hasBankDetails(): boolean {
    return Boolean(
      this.achCreditBankDetails && this.achCreditBankDetails.stripe
    );
  }

  isDeleted(): boolean {
    return this.header.state === 'deleted';
  }

  serialize(): Map<string, any> {
    return this.toMap().withMutations((map) => {
      if (!trim(this.notes)) {
        map.setIn([CONTENT_KEY, CONTENT_KEYS.NOTES], '');
      }

      if (!trim(this.billingAddress)) {
        map.setIn([CONTENT_KEY, CONTENT_KEYS.BILLING_ADDRESS], '');
      }

      if (!trim(this.shippingAddress)) {
        map.setIn([CONTENT_KEY, CONTENT_KEYS.SHIPPING_ADDRESS], '');
      }
    });
  }

  hasValue(ignoreFields?: IIgnoreField): boolean {
    const keys = CONTENT_KEYS;
    const currentValues: Map<string, any> = this.content.toMap();

    return hasValue(
      keys,
      omit(
        CONTENT_SCHEMA,
        [
          ignoreFields[CONTENT_KEYS.BILLING_NAME]
            ? CONTENT_KEYS.BILLING_NAME
            : '',
          CONTENT_KEYS.SHIPPING_ADDRESS_SAME_AS_BILLING,
          CONTENT_KEYS.LOCALE,
        ].map((key: string) => key.toUpperCase())
      ),
      currentValues,
      (value: any, key: string): boolean => {
        const currentValue = currentValues.get(keys[key]);

        // for terms, only check if the current value !== default value to account for the case that `0` is a valid,
        // non-empty terms value
        return (
          (key.toLowerCase() === keys.TERMS ? true : !!currentValue) &&
          currentValue !== value
        );
      }
    );
  }
}
